// Phase 1 site terminology is English-only; the override endpoints still
// require an explicit locale.
export const TERM_LOCALE = "en";

export const ALL_CATEGORIES = "__all__";

export const SHOW_FILTERS = {
	ALL: "All",
	OVERRIDDEN: "Overridden",
	DEFAULT: "Default",
	RECENT: "Recently edited",
};

export const categoryOf = (key) => key.split(".")[0];

// The business-logical row order the legacy Custom Captions screens used
// (model setup -> workbook -> feedback -> service -> defect -> asset). Terms
// without a position here list after these, in the backend's key order.
export const ENTITY_TERM_DISPLAY_ORDER = [
	"modelTemplate",
	"location",
	"arrangement",
	"stage",
	"zone",
	"interval",
	"taskListNo",
	"question",
	"task",
	"actionRequired",
	"operatingMode",
	"system",
	"safetyCritical",
	"part",
	"partQuantity",
	"partName",
	"partDescription",
	"partStockNumber",
	"tool",
	"toolQuantity",
	"toolDescription",
	"lubricant",
	"permit",
	"workbook",
	"purpose",
	"procedure",
	"possibleHazards",
	"additionalPPE",
	"specification",
	"contaminationControls",
	"otherInformation",
	"correctiveActions",
	"isolations",
	"controls",
	"customField1",
	"customField2",
	"customField3",
	"make",
	"model",
	"modelType",
	"serialNumberRange",
	"developerName",
	"role",
	"tutorial",
	"notificationNumber",
	"feedback",
	"changeRequired",
	"benefit",
	"classification",
	"priority",
	"feedbackStatus",
	"user",
	"position",
	"userReference",
	"service",
	"serviceWorkOrder",
	"pauseReason",
	"stopReason",
	"skipReason",
	"defect",
	"defectWorkOrder",
	"defectStatus",
	"defectType",
	"riskRating",
	"riskRatingAction",
	"riskRatingMatrix",
	"department",
	"asset",
	"assetReference",
	"assetPlannerGroup",
	"assetMainWorkCenter",
].map((name) => `entity.${name}`);

const displayOrderIndex = new Map(
	ENTITY_TERM_DISPLAY_ORDER.map((key, index) => [key, index])
);

// "entity.taskListNo" -> "Task List No", "entity.customField1" -> "Custom Field 1"
export const labelize = (key) => {
	const last = key.split(".").pop() ?? "";

	return last
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([a-zA-Z])(\d)/g, "$1 $2")
		.replace(/^./, (c) => c.toUpperCase());
};

// entity.* terms map onto the legacy CustomCaption DTO properties that the
// rest of the app still reads from `me.customCaptions` (entity.asset ->
// asset / assetPlural). Non-entity keys have no legacy counterpart.
export const ccPropertyForKey = (key) =>
	key.startsWith("entity.") ? key.slice("entity.".length) : null;

// Joins the keys endpoint ([{ key, defaults }]) with the override rows
// ([{ key, locale, pluralCategory, value }]) into one editable term model per
// key. Overrides store non-plural values under a null pluralCategory, which
// the upsert endpoint addresses with the "" form key.
export const buildTerms = (
	keys = [],
	overrides = [],
	{ labelFromDefaults = false } = {}
) => {
	const overrideForms = new Map();

	overrides.forEach((row) => {
		const forms = overrideForms.get(row.key) ?? {};
		forms[row.pluralCategory ?? ""] = row.value;
		overrideForms.set(row.key, forms);
	});

	const orderedKeys = [...keys]
		.map((entry, incomingIndex) => ({ entry, incomingIndex }))
		.sort((a, b) => {
			const aIndex = displayOrderIndex.get(a.entry.key) ?? Infinity;
			const bIndex = displayOrderIndex.get(b.entry.key) ?? Infinity;
			return aIndex - bIndex || a.incomingIndex - b.incomingIndex;
		})
		.map(({ entry }) => entry);

	return orderedKeys.map(({ key, defaults = [] }) => {
		const forms = overrideForms.get(key) ?? {};
		const hasPlural = defaults.length > 1;
		const defaultSingular = defaults[0] ?? "";
		const defaultPlural = hasPlural ? defaults[1] : "";
		const overrideSingular =
			(hasPlural ? forms.one ?? forms[""] : forms[""] ?? forms.one) ?? null;
		const overridePlural = forms.other ?? null;
		const effectiveSingular = overrideSingular ?? defaultSingular;
		const effectivePlural = overridePlural ?? defaultPlural;
		// SiteApp screens name each row by the caption it inherits from the
		// application/client template (matching the "Default:" hint), so an
		// overridden row still shows what it deviates from; scope-template
		// screens keep the system field name as the identity.
		const label =
			labelFromDefaults && defaultSingular ? defaultSingular : labelize(key);

		return {
			key,
			category: categoryOf(key),
			label,
			hasPlural,
			defaultSingular,
			defaultPlural,
			overrideSingular,
			overridePlural,
			// Application-level caption edits fan mirror rows into every siteApp, so
			// an override only counts as a siteApp customisation when it differs from
			// the inherited default.
			isOverridden:
				(overrideSingular != null && overrideSingular !== defaultSingular) ||
				(overridePlural != null && overridePlural !== defaultPlural),
			effectiveSingular,
			effectivePlural,
			searchText: [
				key,
				label,
				labelize(key),
				defaultSingular,
				defaultPlural,
				effectiveSingular,
				effectivePlural,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase(),
		};
	});
};

// Substring matches rank first (earlier hit = better); otherwise an ordered
// subsequence match scores by run length. -1 means no match.
export const fuzzyScore = (query, text) => {
	const q = query.toLowerCase();
	const s = text.toLowerCase();

	if (!q) return 0;

	const idx = s.indexOf(q);
	if (idx >= 0) return 1000 - idx;

	let from = 0;
	let score = 0;
	let run = 0;

	for (const ch of q) {
		const found = s.indexOf(ch, from);
		if (found === -1) return -1;

		run = found === from ? run + 1 : 1;
		score += run;
		from = found + 1;
	}

	return score;
};

export const filterTerms = (
	terms,
	{
		query = "",
		category = ALL_CATEGORIES,
		show = SHOW_FILTERS.ALL,
		recentKeys = {},
	} = {}
) => {
	let list = terms;

	if (category !== ALL_CATEGORIES) {
		list = list.filter((t) => t.category === category);
	}

	if (show === SHOW_FILTERS.OVERRIDDEN) {
		list = list.filter((t) => t.isOverridden);
	} else if (show === SHOW_FILTERS.DEFAULT) {
		list = list.filter((t) => !t.isOverridden);
	} else if (show === SHOW_FILTERS.RECENT) {
		list = list
			.filter((t) => recentKeys[t.key])
			.sort((a, b) => recentKeys[b.key] - recentKeys[a.key]);
	}

	const q = query.trim();
	if (!q) return list;

	return list
		.map((t) => ({ term: t, score: fuzzyScore(q, t.searchText) }))
		.filter((x) => x.score >= 0)
		.sort((a, b) => b.score - a.score)
		.map((x) => x.term);
};

// Keeps the legacy `me.customCaptions` blob (read synchronously all over the
// app) in step with a saved or reset entity.* term — the same in-place
// storage update the old Custom Captions screen performed.
export const syncCustomCaptionStorage = (term, { singular, plural }) => {
	const prop = ccPropertyForKey(term.key);
	if (!prop) return;

	const stored =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	if (!stored?.customCaptions) return;

	const updated = {
		...stored,
		customCaptions: {
			...stored.customCaptions,
			[prop]: singular,
			...(term.hasPlural ? { [`${prop}Plural`]: plural } : {}),
		},
	};

	localStorage.setItem("me", JSON.stringify(updated));
	sessionStorage.setItem("me", JSON.stringify(updated));
};

export const csvEscape = (value) => {
	let s = value == null ? "" : String(value);
	// Spreadsheet formula-injection guard: a leading =, +, - or @ would execute
	// as a formula when an exported CSV is opened in Excel. The apostrophe makes
	// it inert; import strips it back off (stripFormulaGuard).
	if (/^[=+\-@]/.test(s)) s = `'${s}`;
	return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const stripFormulaGuard = (s) => (/^'[=+\-@]/.test(s) ? s.slice(1) : s);

// Minimal RFC-4180-ish parser: handles quoted fields with embedded commas,
// quotes ("") and newlines.
export const parseCsv = (text) => {
	const rows = [];
	let row = [];
	let field = "";
	let inQuotes = false;

	for (let i = 0; i < text.length; i++) {
		const c = text[i];
		if (inQuotes) {
			if (c === '"') {
				if (text[i + 1] === '"') {
					field += '"';
					i++;
				} else {
					inQuotes = false;
				}
			} else {
				field += c;
			}
		} else if (c === '"' && field === "") {
			// A quote only opens a quoted field at field start (RFC 4180); a bare
			// mid-field quote (6" Duct) is literal text, not a quoting error that
			// should swallow the rest of the row.
			inQuotes = true;
		} else if (c === ",") {
			row.push(field);
			field = "";
		} else if (c === "\n") {
			row.push(field);
			rows.push(row);
			row = [];
			field = "";
		} else if (c !== "\r") {
			field += c;
		}
	}
	if (field !== "" || row.length) {
		row.push(field);
		rows.push(row);
	}
	return rows;
};

// Resolves the CSV header into the key column plus one column pair per language
// the file actually contains. Returns null when the required columns are absent.
export const parseImportHeader = (headerRow, enabledLocales) => {
	const header = headerRow.map((h) => h.trim().toLowerCase());
	const keyIdx = header.indexOf("key");
	const localeColumns = enabledLocales
		.map((locale) => ({
			locale,
			singularIdx: header.indexOf(locale.toLowerCase()),
			pluralIdx: header.indexOf(`${locale.toLowerCase()} (plural)`),
		}))
		.filter((column) => column.singularIdx >= 0);

	if (keyIdx < 0 || !localeColumns.length) return null;
	return { keyIdx, localeColumns };
};

// Diffs imported rows against each language's current values, returning only
// the cells that actually change - split into brand-new overrides vs ones that
// replace an existing override. A missing "(plural)" column or a blank plural
// cell means "no change to the plural": it must compare (and write) as the
// current value, or a singular-only CSV would mark every plural-bearing term
// changed and pin the whole catalogue as overridden.
export const diffImportRows = (rows, { keyIdx, localeColumns }, localeTermMaps) => {
	const changes = [];
	for (const row of rows) {
		const key = (row[keyIdx] ?? "").trim();
		if (!key) continue;

		for (const column of localeColumns) {
			const localeTerm = localeTermMaps.get(column.locale)?.get(key);
			if (!localeTerm) continue;

			const singular = stripFormulaGuard((row[column.singularIdx] ?? "").trim());
			const pluralCell =
				column.pluralIdx >= 0 ? (row[column.pluralIdx] ?? "").trim() : "";
			const plural = pluralCell
				? stripFormulaGuard(pluralCell)
				: localeTerm.effectivePlural ?? "";

			// Values can't be stored empty, so a blank singular is skipped.
			if (!singular) continue;

			// Only surface cells that change the current value; an untouched
			// export is a no-op.
			if (
				singular === localeTerm.effectiveSingular &&
				(!localeTerm.hasPlural || plural === localeTerm.effectivePlural)
			) {
				continue;
			}

			const newPlural = localeTerm.hasPlural ? plural : "";
			changes.push({
				key,
				label: localeTerm.label,
				locale: column.locale,
				hasPlural: localeTerm.hasPlural,
				oldSingular: localeTerm.effectiveSingular,
				oldPlural: localeTerm.effectivePlural,
				newSingular: singular,
				newPlural,
				// An existing override being changed = overwrite; otherwise it's a
				// brand-new override on top of the shipped default.
				isOverwrite: localeTerm.isOverridden,
				forms: localeTerm.hasPlural
					? { one: singular, other: newPlural }
					: { "": singular },
			});
		}
	}
	return changes;
};
